import { schnorr } from '@noble/curves/secp256k1';
import { bech32 } from '@scure/base';
import { bytesToHex, hexToBytes, isHex32 } from './util.js';

/**
 * Herald identities are secp256k1 keypairs used with BIP-340 Schnorr signatures.
 * Public keys are 32-byte x-only. Encoding is NIP-19 bech32, so `npub`/`nsec`
 * strings are interchangeable with the whole Nostr tooling ecosystem.
 */

const BECH32_LIMIT = 1000;

export function generateSecretKey() {
  return schnorr.utils.randomPrivateKey();
}

export function getPublicKey(secretKey) {
  return bytesToHex(schnorr.getPublicKey(normalizeSecret(secretKey)));
}

export function normalizeSecret(secretKey) {
  if (secretKey instanceof Uint8Array) return secretKey;
  if (typeof secretKey === 'string') {
    if (secretKey.startsWith('nsec1')) return decodeBech32('nsec', secretKey);
    if (isHex32(secretKey)) return hexToBytes(secretKey);
  }
  throw new Error('invalid secret key: expected 32-byte hex, nsec1..., or Uint8Array');
}

/** Accepts hex pubkey or npub1..., returns 32-byte lowercase hex. */
export function normalizePubkey(pubkey) {
  if (pubkey instanceof Uint8Array) return bytesToHex(pubkey);
  if (typeof pubkey === 'string') {
    if (pubkey.startsWith('npub1')) return bytesToHex(decodeBech32('npub', pubkey));
    const lower = pubkey.toLowerCase();
    if (isHex32(lower)) return lower;
  }
  throw new Error(`invalid public key: ${String(pubkey).slice(0, 24)}`);
}

function decodeBech32(expectedPrefix, value) {
  const { prefix, words } = bech32.decode(value, BECH32_LIMIT);
  if (prefix !== expectedPrefix) throw new Error(`expected ${expectedPrefix}, got ${prefix}`);
  const bytes = bech32.fromWords(words);
  if (bytes.length !== 32) throw new Error(`expected 32 bytes, got ${bytes.length}`);
  return new Uint8Array(bytes);
}

function encodeBech32(prefix, bytes) {
  return bech32.encode(prefix, bech32.toWords(bytes), BECH32_LIMIT);
}

export function npubEncode(pubkeyHex) { return encodeBech32('npub', hexToBytes(normalizePubkey(pubkeyHex))); }
export function nsecEncode(secretKey) { return encodeBech32('nsec', normalizeSecret(secretKey)); }

/** Short human-facing fingerprint, e.g. "npub1abcd…wxyz". Never use for comparison. */
export function fingerprint(pubkey) {
  const npub = npubEncode(pubkey);
  return `${npub.slice(0, 12)}…${npub.slice(-6)}`;
}
